import type { ColDef, GridApi } from 'ag-grid-community';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import styled from 'styled-components';
import { Box, Flex, HStack, Stack } from '../../Core';
import {
  InlineFormattedNumberContext,
  useInlineFormattedNumberContext,
} from '../../FormattedNumber/InlineFormattedNumberContext';
import { ICON_SIZES, Icon, IconName } from '../../Icons';
import type { RenderCellOptions } from '../useRenderCell';
import { safeGridApi } from '../utils';

export interface MobileRowDetailRowsProps<TRow> {
  api: GridApi<TRow>;
  /** Render function from {@link useRenderCell} — produces a real cell using the column's renderer. */
  renderCell: (colId: string, options?: RenderCellOptions) => ReactNode;
  /** Column IDs to skip (e.g. `'warning'`, action-button columns). */
  excludeColumnIds?: string[];
  /** Optional dynamic bottom padding to clear a sticky footer. */
  footerHeight?: number;
}

/**
 * Renders every visible column of a row as a label/value pair, using each
 * column's existing ag-grid cell renderer. Designed to be embedded inside a
 * sheet/drawer so the detail view stays consistent with the user's chosen
 * column visibility from the desktop blotter.
 *
 * Horizontal padding is intentionally absent — the parent container is
 * expected to supply it so the padding applies consistently to both the rows
 * and any surrounding content (header, footer).
 */
export function MobileRowDetailRows<TRow>({
  api,
  renderCell,
  excludeColumnIds,
  footerHeight = 0,
}: MobileRowDetailRowsProps<TRow>) {
  // We only need to force a render when ag-grid column visibility changes.
  // The value itself is irrelevant, so we intentionally ignore it.
  const [, setColumnVisibilityVersion] = useState(0);

  // Values are always right-aligned here (see MobileDetailValue below), so the truncated-value
  // expansion must anchor right too, regardless of the source column's own `align` config.
  const ambientFormattedNumberContext = useInlineFormattedNumberContext();
  const formattedNumberContextValue = useMemo(
    () => ({ ...ambientFormattedNumberContext, forceAlign: 'right' as const }),
    [ambientFormattedNumberContext]
  );

  useEffect(() => {
    const onColumnVisibilityChanged = () => {
      setColumnVisibilityVersion(prev => prev + 1);
    };
    safeGridApi(api)?.addEventListener('columnVisible', onColumnVisibilityChanged);
    return () => {
      safeGridApi(api)?.removeEventListener('columnVisible', onColumnVisibilityChanged);
    };
  }, [api]);

  // Collect all "member" column IDs that belong to any toggleable group so
  // we can style grouped rows consistently in the mobile detail view.
  const groupedColumnIds = (() => {
    const allColumns = safeGridApi(api)?.getColumns() ?? [];
    const groupedColumnIdSet = new Set<string>();
    allColumns.forEach(column => {
      getColumnsInGroup(column.getColDef()).forEach(groupedColumnIdSet.add, groupedColumnIdSet);
    });
    return groupedColumnIdSet;
  })();

  const visibleColumns = (() => {
    const cols = safeGridApi(api)?.getColumns() ?? [];
    const exclude = new Set(excludeColumnIds ?? []);
    return cols.filter(col => {
      if (!col.isVisible()) {
        return false;
      }
      const colId = col.getColId();
      if (exclude.has(colId)) {
        return false;
      }
      const def = col.getColDef();
      const headerName = api.getDisplayNameForColumn(col, 'header');
      if (!headerName) {
        return false;
      }
      if (def.colId?.startsWith('ag-Grid-')) {
        return false;
      }
      return true;
    });
  })();

  const handleToggleColumnGroup = (columnsInGroup: string[], allColumnsInGroupCollapsed: boolean) => {
    safeGridApi(api)?.setColumnsVisible(columnsInGroup, allColumnsInGroupCollapsed);
  };

  return (
    <InlineFormattedNumberContext.Provider value={formattedNumberContextValue}>
      <Stack gap="spacing12" w="100%">
        {visibleColumns.map(col => {
          const colId = col.getColId();
          const headerName = api.getDisplayNameForColumn(col, 'header');
          const colDef = col.getColDef();
          const columnsInGroup = getColumnsInGroup(colDef);
          const isToggleColumn = columnsInGroup.length > 0;
          const rowInGroupedColumns = isToggleColumn || groupedColumnIds.has(colId);
          const allColumns = safeGridApi(api)?.getAllGridColumns();
          // Mirror desktop behavior: when every group member is hidden, show the
          // "Columns" icon (collapsed state); otherwise show expand icon.
          const allColumnsInGroupCollapsed =
            isToggleColumn &&
            (allColumns
              ? columnsInGroup.every(columnId => {
                  const groupedColumn = allColumns.find(gridColumn => gridColumn.getColId() === columnId);
                  if (groupedColumn) {
                    return !groupedColumn.isVisible();
                  }
                  return true;
                })
              : false);

          return (
            <MobileDetailRow key={colId} grouped={rowInGroupedColumns}>
              <HStack alignItems="center" gap="spacing2">
                <Box color="colorTextDefault" fontSize="fontSizeDefault">
                  {headerName}
                </Box>
                {isToggleColumn && (
                  <HStack
                    px="spacing12"
                    py="spacing4"
                    onClick={() => handleToggleColumnGroup(columnsInGroup, allColumnsInGroupCollapsed)}
                    data-testid="mobile-row-detail-group-toggle-button"
                  >
                    <Icon
                      icon={allColumnsInGroupCollapsed ? IconName.Columns : IconName.ExpandLeft}
                      size={ICON_SIZES.SMALL}
                    />
                  </HStack>
                )}
              </HStack>
              <MobileDetailValue
                justifyContent="flex-end"
                flex="1 1 auto"
                color="colorTextImportant"
                ml="auto"
                fontSize="fontSizeDefault"
              >
                {renderCell(colId)}
              </MobileDetailValue>
            </MobileDetailRow>
          );
        })}
      </Stack>
    </InlineFormattedNumberContext.Provider>
  );
}

function getColumnsInGroup(colDef: ColDef): string[] {
  const headerComponentParams = colDef.headerComponentParams;
  if (headerComponentParams && typeof headerComponentParams === 'object' && 'columnsInGroup' in headerComponentParams) {
    const maybeColumnsInGroup = (headerComponentParams as { columnsInGroup?: unknown }).columnsInGroup;
    if (Array.isArray(maybeColumnsInGroup) && maybeColumnsInGroup.every(columnId => typeof columnId === 'string')) {
      return maybeColumnsInGroup;
    }
  }
  return [];
}

const MobileDetailRow = styled(Flex)<{ grouped: boolean }>`
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing12}px;
  ${({ grouped, theme }) =>
    grouped
      ? `
      background: ${theme.colors.gray['020']};
      margin: -6px -16px;
      padding: 6px 16px;
    `
      : ''}
`;

const MobileDetailValue = styled(Flex)`
  min-width: 0;
  overflow-wrap: anywhere;
  word-break: break-word;

  & > * {
    min-width: 0;
    max-width: 100%;
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: break-word;
  }
`;
